import type { TilePosition } from '../../../model/gamestate/GameState';
import { drawCachedActorSprite, getCachedActorSpriteDrawSize } from '../../ActorSpriteCache';
import { drawMobVisual, getMobVisualDrawSize } from '../../mobVisuals/index';
import { drawCachedLabel, drawCachedSpeechBubble, getCachedLabelSize, getCachedSpeechBubbleSize } from '../../textSpriteCache';
import { COLORS } from '../constants';
import { isRectVisible, type ScreenBounds } from '../culling';
import type { EntityManager } from '../EntityManager';
import type { MapLayerRenderer } from '../MapLayerRenderer';
import type { ActorVisual } from '../types';
import type { Viewport } from '../viewport';
import { getSpeechBubble, hasActiveSpeechBubbles } from '../../../chat/speechBubbles';

interface ExtendedActorVisual extends ActorVisual {
    isMoving: boolean;
}

export class ActorRenderer {
    constructor(
        private readonly tileSize: number,
        private readonly getActiveRadius: () => number,
    ) { }

    public drawActors(
        ctx: CanvasRenderingContext2D,
        entityManager: EntityManager,
        lastCharacterPosition: TilePosition | null,
        viewport: Viewport,
        mapLayer: MapLayerRenderer,
        viewportBounds: ScreenBounds,
    ): boolean {
        const actors: ExtendedActorVisual[] = [];
        const grid = mapLayer.getGrid();
        const nowMs = performance.now();

        for (const state of entityManager.getActorVisuals().values()) {
            const alphaMultiplier = lastCharacterPosition && !this.isWithinActiveRadius(state.current, lastCharacterPosition, grid) ? 0.35 : 1;
            const alpha = state.opacity * alphaMultiplier;
            if (alpha <= 0) continue;
            const actor = state.data;
            const center = viewport.worldCenterToScreen(state.currentCenter);
            const isMoving = Math.abs(state.currentCenter.x - state.targetCenter.x) > 0.01 || Math.abs(state.currentCenter.y - state.targetCenter.y) > 0.01;
            const rotation = actor.rotation ?? 180;
            const fallbackColor = actor.kind === 'mob'
                ? (actor.entityType === 'zombie' ? COLORS.zombie : COLORS.enemy)
                : COLORS.player;
            actors.push({
                id: actor.id,
                playerId: actor.playerId,
                kind: actor.kind,
                entityType: actor.entityType,
                center,
                rotation,
                color: actor.color ?? fallbackColor,
                accessoriesJson: actor.accessoriesJson,
                label: actor.kind === 'mob' ? undefined : actor.displayName,
                hp: actor.hp,
                maxHp: actor.maxHp,
                stamina: actor.stamina,
                maxStamina: actor.maxStamina,
                alpha,
                isMoving,
            });
        }

        for (const actor of actors) {
            if (!this.isActorVisible(actor, viewportBounds, nowMs)) {
                continue;
            }
            this.drawActor(ctx, actor, nowMs);
        }
        return hasActiveSpeechBubbles(nowMs);
    }

    private drawActor(ctx: CanvasRenderingContext2D, actor: ExtendedActorVisual, nowMs: number): void {
        const cx = actor.center.x;
        const cy = actor.center.y;

        ctx.save();
        if (actor.entityType && actor.entityType !== 'player') {
            drawMobVisual(ctx, {
                entityType: actor.entityType,
                centerX: cx,
                centerY: cy,
                size: this.tileSize,
                rotation: actor.rotation,
                color: actor.color,
                alpha: actor.alpha,
                isMoving: actor.isMoving,
            });
        } else {
            drawCachedActorSprite(ctx, {
                centerX: cx,
                centerY: cy,
                size: this.tileSize,
                rotation: actor.rotation,
                color: actor.color,
                accessoriesJson: actor.accessoriesJson,
                alpha: actor.alpha,
            });
        }

        if (actor.label) {
            drawCachedLabel(ctx, {
                text: actor.label,
                centerX: cx,
                centerY: cy - this.tileSize * 0.65,
                fontPx: Math.max(12, this.tileSize * 0.26),
                alpha: actor.alpha,
            });
        }

        const speechKey = actor.kind === 'self' ? '__self__' : actor.playerId;
        if (speechKey) {
            const speech = getSpeechBubble(speechKey, nowMs);
            if (speech) {
                drawCachedSpeechBubble(ctx, {
                    text: speech.text,
                    centerX: cx,
                    baselineY: cy - this.tileSize * 1.2,
                    fontPx: Math.max(12, this.tileSize * 0.24),
                    maxWidth: this.tileSize * 3.8,
                    alpha: actor.alpha,
                });
            }
        }

        if (typeof actor.hp === 'number' && typeof actor.maxHp === 'number') {
            this.drawResourceBar(ctx, cx, cy, actor.hp, actor.maxHp, 4, (ratio) => {
                const r = Math.floor(255 * (1 - ratio));
                const g = Math.floor(255 * ratio);
                return `rgb(${r}, ${g}, 60)`;
            });
        }
        if (typeof actor.stamina === 'number' && typeof actor.maxStamina === 'number') {
            this.drawResourceBar(ctx, cx, cy, actor.stamina, actor.maxStamina, 10, () => '#22c55e');
        }
        ctx.restore();
    }

    private drawResourceBar(
        ctx: CanvasRenderingContext2D,
        cx: number,
        cy: number,
        current: number,
        max: number,
        offsetY: number,
        colorForRatio: (ratio: number) => string
    ): void {
        if (max <= 0) return;
        const barWidth = this.tileSize * 0.7;
        const barHeight = 4;
        const barX = cx - barWidth / 2;
        const barY = cy + this.tileSize / 3 + offsetY;
        const ratio = Math.max(0, Math.min(1, current / max));
        ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
        ctx.fillRect(barX, barY, barWidth, barHeight);
        ctx.fillStyle = colorForRatio(ratio);
        ctx.fillRect(barX, barY, barWidth * ratio, barHeight);
    }

    private isActorVisible(actor: ActorVisual, viewportBounds: ScreenBounds, nowMs: number): boolean {
        const spriteSize = actor.entityType && actor.entityType !== 'player'
            ? getMobVisualDrawSize(actor.entityType, this.tileSize)
            : getCachedActorSpriteDrawSize(this.tileSize);
        let boundsLeft = actor.center.x - spriteSize / 2;
        let boundsTop = actor.center.y - spriteSize / 2;
        let boundsRight = actor.center.x + spriteSize / 2;
        let boundsBottom = actor.center.y + spriteSize / 2;

        if (actor.label) {
            const labelSize = getCachedLabelSize(actor.label, Math.max(12, this.tileSize * 0.26));
            boundsLeft = Math.min(boundsLeft, actor.center.x - labelSize.width / 2);
            boundsTop = Math.min(boundsTop, (actor.center.y - this.tileSize * 0.65) - labelSize.height / 2);
            boundsRight = Math.max(boundsRight, actor.center.x + labelSize.width / 2);
        }

        const speechKey = actor.kind === 'self' ? '__self__' : actor.playerId;
        const speech = speechKey ? getSpeechBubble(speechKey, nowMs) : null;
        if (speech) {
            const bubbleSize = getCachedSpeechBubbleSize(
                speech.text,
                Math.max(12, this.tileSize * 0.24),
                this.tileSize * 3.8,
            );
            const bubbleLeft = actor.center.x - bubbleSize.width / 2;
            const bubbleTop = (actor.center.y - this.tileSize * 1.2) - bubbleSize.bubbleHeight;
            boundsLeft = Math.min(boundsLeft, bubbleLeft);
            boundsTop = Math.min(boundsTop, bubbleTop);
            boundsRight = Math.max(boundsRight, bubbleLeft + bubbleSize.width);
        }

        boundsBottom = Math.max(boundsBottom, actor.center.y + this.tileSize * 0.9);
        return isRectVisible(viewportBounds, boundsLeft, boundsTop, boundsRight - boundsLeft, boundsBottom - boundsTop);
    }

    private isWithinActiveRadius(tile: TilePosition, center: TilePosition, grid: { distance: (cx: number, cy: number, x: number, y: number) => number }): boolean {
        return grid.distance(Math.floor(center.x), Math.floor(center.y), tile.x, tile.y) <= this.getActiveRadius();
    }
}
